import { useMemo, useState } from 'react'
import { formatDealPrice } from '@/components/deals-table'
import { platformLogo } from '@/lib/platforms'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { PencilIcon, PlusIcon } from 'lucide-react'
import {
	Badge, Button,
	DealCard, DealCardFields, DealCardField,
	IconButton,
} from '@rentflowdev/ui'
import { useGetContactQuery, useGetPropertyQuery, useListDealMeetingsQuery } from '@/api'
import { formatPhone } from '@/lib/phone'
import { formatPropertyAddress } from '@/lib/property-address'
import type { Deal, DealMeeting, Property, RentPeriod } from '@/types'
import { CURRENCY_SIGNS, DEFAULT_LOCALE } from '@/types'
import { EditContactDialog } from '@/components/edit-contact-dialog'
import { EditRentTermsDialog } from '@/components/edit-rent-terms-dialog'


const PERIOD_LABELS: Record<RentPeriod, string> = {
	WEEKLY:    'в неделю',
	MONTHLY:   'в месяц',
	QUARTERLY: 'в квартал',
	YEARLY:    'в год',
}

function buildPropertyTitle(p: Property | undefined): string | undefined {
	if (!p) return undefined
	const kind = p.type === 'APARTMENT' ? 'квартира' : p.type === 'HOUSE' ? 'дом' : p.type === 'ROOM' ? 'комната' : p.type === 'OFFICE' ? 'офис' : ''
	const head = [p.rooms ? `${p.rooms}-к` : undefined, kind].filter(Boolean).join(' ')
	const tail = formatPropertyAddress(p.city, p.address)
	const joined = [head, tail].filter(Boolean).join(' ')
	return joined || undefined
}

function buildPropertyPrice(p: Property | undefined): string | undefined {
	const rt = p?.rentTerms
	if (!rt?.rentAmount) return undefined
	const amount = Number(rt.rentAmount).toLocaleString('ru-RU')
	const symbol = CURRENCY_SIGNS[rt.rentCurrency] ?? rt.rentCurrency
	const period = rt.rentPeriod ? ` ${PERIOD_LABELS[rt.rentPeriod] ?? ''}` : ''
	return `${amount} ${symbol}${period}`.trim()
}

function pickSummaryMeeting(meetings: DealMeeting[]): DealMeeting | undefined {
	const now = Date.now()
	const upcoming = meetings
		.filter((m) => (m.status === 'SCHEDULED' || m.status === 'CONFIRMED') && new Date(m.scheduledAt).getTime() >= now)
		.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
	return upcoming[0]
}

function formatMeetingDate(iso: string): string {
	const date = new Date(iso)
	const day  = date.toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'short' })
	const time = date.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	return `${day} в ${time}`
}

interface DealInfoSectionProps {
	deal: Deal
	/** Switches the page to the Meetings tab — used by both the empty-state CTA and the edit pencil. */
	onOpenMeetings?: () => void
}

export function DealInfoSection({ deal, onOpenMeetings }: DealInfoSectionProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { data: contact } = useGetContactQuery(deal.contactId ?? '', {
		skip: !deal.contactId,
	})
	const { data: property } = useGetPropertyQuery(deal.propertyId ?? '', {
		skip: !deal.propertyId,
	})
	const { data: meetingsData } = useListDealMeetingsQuery({ dealId: deal.id })
	const summaryMeeting = useMemo(() => pickSummaryMeeting(meetingsData?.items ?? []), [meetingsData])
	const propertyTitle = deal.propertyTitle ?? buildPropertyTitle(property ?? undefined)
	const propertyPrice = formatDealPrice(deal.propertyPrice) ?? buildPropertyPrice(property ?? undefined)
	const [editContactOpen, setEditContactOpen] = useState(false)
	const [editTermsOpen, setEditTermsOpen] = useState(false)

	return (
		<div className="flex flex-col gap-4">
			<section className="flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<h2 className="text-base leading-6 font-[500] text-(--color-foreground)">
						{t('deals.detail.meeting')}
					</h2>
					{/* Meetings are edited on their own tab rather than in a dialog, so
					    this jumps there instead of opening one — matching the pencils
					    on the sections below, which do open dialogs. */}
					{summaryMeeting && onOpenMeetings && (
						<IconButton
							variant="ghost"
							size="sm"
							aria-label={t('deals.detail.editMeeting')}
							onClick={onOpenMeetings}
						>
							<PencilIcon className="size-4" />
						</IconButton>
					)}
				</div>
				<DealCard>
					{summaryMeeting ? (
						<DealCardFields>
							<DealCardField label={t('deals.detail.meetingDateTime')}>
								<Badge variant="warning" size="sm">{formatMeetingDate(summaryMeeting.scheduledAt)}</Badge>
							</DealCardField>
							<DealCardField label={t('deals.detail.meetingAddress')}>{summaryMeeting.address}</DealCardField>
						</DealCardFields>
					) : (
						<div className="flex items-center justify-between gap-3 py-1">
							<p className="text-sm text-(--color-muted-foreground)">{t('deals.meetings.summaryEmpty')}</p>
							{onOpenMeetings && (
								<Button
									variant="outline"
									size="sm"
									iconLeft={<PlusIcon className="size-4" />}
									onClick={onOpenMeetings}
								>
									{t('deals.meetings.summaryCta')}
								</Button>
							)}
						</div>
					)}
				</DealCard>
			</section>

			<section className="flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<h2 className="text-base leading-6 font-[500] text-(--color-foreground)">
						{t('deals.detail.aboutDeal')}
					</h2>
					{property && (
						<IconButton
							variant="ghost"
							size="sm"
							aria-label={t('deals.detail.editTerms')}
							onClick={() => setEditTermsOpen(true)}
						>
							<PencilIcon className="size-4" />
						</IconButton>
					)}
				</div>
				<DealCard>
					<DealCardFields>
						{propertyTitle && (
							<DealCardField label={t('deals.detail.property')}>
								{deal.propertyId ? (
									<button
										type="button"
										className="text-left text-sm leading-5 text-(--color-primary) underline-offset-2 hover:underline"
										onClick={() => void navigate({ to: '/properties/$propertyId', params: { propertyId: deal.propertyId! } })}
									>
										{propertyTitle}
									</button>
								) : propertyTitle}
							</DealCardField>
						)}
						{propertyPrice && (
							<DealCardField label={t('deals.detail.price')}>{propertyPrice}</DealCardField>
						)}
						{deal.source && (
							<DealCardField label={t('deals.detail.source')}>
								{platformLogo(deal.source)
									? <Badge variant="logo" size="sm" logoSrc={platformLogo(deal.source)} logoAlt={deal.source} />
									: <Badge variant="secondary" size="sm">{deal.source}</Badge>
								}
							</DealCardField>
						)}
						<DealCardField label={t('deals.detail.createdAt')}>
							{new Date(deal.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
						</DealCardField>
					</DealCardFields>
				</DealCard>
			</section>

			<section className="flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<h2 className="text-base leading-6 font-[500] text-(--color-foreground)">
						{t('deals.detail.client')}
					</h2>
					{contact && (
						<IconButton
							variant="ghost"
							size="sm"
							aria-label={t('deals.detail.edit')}
							onClick={() => setEditContactOpen(true)}
						>
							<PencilIcon className="size-4" />
						</IconButton>
					)}
				</div>
				<DealCard>
					<DealCardFields>
						{(contact?.name ?? deal.clientName) && (
							<DealCardField label={t('deals.detail.clientName')}>
								{contact?.name ?? deal.clientName}
							</DealCardField>
						)}
						<DealCardField label={t('deals.detail.clientCompany')}>
							{/* Из контакта, как имя, телефон и почта строкой выше:
							    clientCompany на сделке — слепок, и после правки
							    контакта он обновляется отдельным запросом. */}
							{contact?.company ?? deal.clientCompany ?? t('deals.detail.clientCompanyMissing')}
						</DealCardField>
						<DealCardField label={t('deals.detail.clientPhone')}>
							{formatPhone(contact?.phone ?? deal.clientPhone) || t('deals.detail.clientEmailMissing')}
						</DealCardField>
						<DealCardField label={t('deals.detail.clientEmail')}>
							{contact?.email ?? deal.clientEmail ?? t('deals.detail.clientEmailMissing')}
						</DealCardField>
					</DealCardFields>
				</DealCard>
			</section>

			{contact && (
				<EditContactDialog
					contact={contact}
					open={editContactOpen}
					onOpenChange={setEditContactOpen}
				/>
			)}
			{property && (
				<EditRentTermsDialog
					property={property}
					open={editTermsOpen}
					onOpenChange={setEditTermsOpen}
				/>
			)}
		</div>
	)
}
